'use strict'

import {
    FOR_YOU_CATEGORY_ATTRIBUTE,
    FYP_DYNAMITE_COLOR_ATTRIBUTE,
    FYP_GARAGE_COLOR_ATTRIBUTE,
    FYP_NAV_COLOR_DEFAULT
} from '../constants'

export const FOR_YOU_TITLE_MAX_NAME_LENGTH = 20

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

/**
 * True when `value` is a valid CSS hex colour (`#RGB`, `#RGBA`, `#RRGGBB`, or
 * `#RRGGBBAA`). Anything else — empty, missing, or malformed like `#12345` — is
 * invalid.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const isValidHexColor = (value) =>
    typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim())

/**
 * Resolves the "For You" accent colour, falling back to `fallback` (black) when
 * the configured value is empty or not a valid hex colour.
 *
 * @param {*} rawColor - The value configured in Business Manager.
 * @param {string} [fallback=FYP_NAV_COLOR_DEFAULT]
 * @returns {string} A valid hex colour.
 */
export const resolveForYouColor = (rawColor, fallback = FYP_NAV_COLOR_DEFAULT) => {
    const trimmed = typeof rawColor === 'string' ? rawColor.trim() : ''
    return isValidHexColor(trimmed) ? trimmed : fallback
}

// Brand -> the category attribute that carries that brand's "For You" nav colour.
const FYP_COLOR_ATTRIBUTE_BY_BRAND = {
    garage: FYP_GARAGE_COLOR_ATTRIBUTE,
    dynamite: FYP_DYNAMITE_COLOR_ATTRIBUTE
}

/**
 * Resolves the "For You" navigation colour for a category, scoped to the current
 * brand: Garage reads `c_fypGarageColor`, Dynamite reads `c_fypDynamiteColor` (both
 * authored on the category in Business Manager). Falls back to black when the
 * attribute is missing, empty, or not a valid hex colour — or when the brand is
 * unknown.
 *
 * @param {Object} category - The category carrying the colour attributes.
 * @param {string} brandId - 'garage' | 'dynamite'
 * @param {string} [fallback=FYP_NAV_COLOR_DEFAULT]
 * @returns {string} A valid hex colour (never undefined).
 */
export const resolveForYouCategoryColor = (
    category,
    brandId,
    fallback = FYP_NAV_COLOR_DEFAULT
) => {
    const attribute = FYP_COLOR_ATTRIBUTE_BY_BRAND[brandId]
    if (!attribute || !category) return fallback

    return resolveForYouColor(category[attribute], fallback)
}

export const getForYouName = (firstName) => {
    const name = typeof firstName === 'string' ? firstName.trim() : ''
    if (!name || name.length > FOR_YOU_TITLE_MAX_NAME_LENGTH) return null
    return name
}

export const isForYouCategory = (category) =>
    category?.[FOR_YOU_CATEGORY_ATTRIBUTE] === true

export const findForYouCategory = (node, itemsKey = 'categories') => {
    if (!node) return null
    if (isForYouCategory(node)) return node

    const children = Array.isArray(node[itemsKey]) ? node[itemsKey] : []
    for (const child of children) {
        const found = findForYouCategory(child, itemsKey)
        if (found) return found
    }

    return null
}

export const filterForYouCategoryTree = (node, itemsKey, isForYouMasterEnabled) => {
    if (!node || !Array.isArray(node[itemsKey]) || node[itemsKey].length === 0) {
        return node
    }
    return {
        ...node,
        [itemsKey]: node[itemsKey]
            .filter((child) => {
                if (!child) return false
                if (isForYouCategory(child) && !isForYouMasterEnabled) return false
                return true
            })
            .map((child) => filterForYouCategoryTree(child, itemsKey, isForYouMasterEnabled))
    }
}
